import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { communityService } from '../services/api';
import { useTranslation } from 'react-i18next';

/**
 * Shared hook for kick/ban actions across dashboard, user profile, and product details.
 * Handles optimistic invalidation and returns toast state for callers to render.
 */
export function useCommunityModeration() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const kickFromCommunity = useCallback(
    async (communityId, userId) => {
      try {
        await communityService.removeUser(communityId, userId);
        queryClient.invalidateQueries({ queryKey: ['community-members', communityId] });
        queryClient.invalidateQueries({ queryKey: ['community', communityId] });
        showToast('success', t('Usuario expulsado de la comunidad'));
        return true;
      } catch (err) {
        showToast('error', err?.response?.data?.message || t('Error al expulsar al usuario'));
        return false;
      }
    },
    [queryClient, showToast, t]
  );

  const banFromCommunity = useCallback(
    async (communityId, userId, reason = null) => {
      try {
        await communityService.blockUser(communityId, userId, reason);
        queryClient.invalidateQueries({ queryKey: ['community-members', communityId] });
        queryClient.invalidateQueries({ queryKey: ['community-blocks', communityId] });
        queryClient.invalidateQueries({ queryKey: ['community', communityId] });
        showToast('success', t('Usuario bloqueado de la comunidad'));
        return true;
      } catch (err) {
        showToast('error', err?.response?.data?.message || t('Error al bloquear al usuario'));
        return false;
      }
    },
    [queryClient, showToast, t]
  );

  return { kickFromCommunity, banFromCommunity, toast };
}
